import React, { lazy, Suspense } from 'react';
import type { ShopLegalInfoType } from 'graphql-ts-types';

import { useOpenDrawer } from '../../hooks/drawer';
import { useLocalization } from '../../providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import type { SellerContactParams } from '../SellerContactContent/utils';
import { CtaType } from '../SellerContactContent/utils';
import { CenteredSpinner } from '../Spinner';
import type { SellerDrawerTracking } from './types';

export interface OpenSellerDrawerOptions {
  shopId: string;
  isHome24: boolean;
  withSellerKPIs: boolean;
  tracking?: SellerDrawerTracking;
  expandedSectionKey?: ShopLegalInfoType;
  ctaType?: CtaType;
  sellerContactParams?: SellerContactParams;
}

const SellerDrawerContent = lazy(() => import('./SellerDrawerContent'));

export const useSellerDrawer = ({
  shopId,
  isHome24,
  tracking,
  withSellerKPIs,
  expandedSectionKey,
  ctaType = CtaType.ViewProducts,
  sellerContactParams,
}: OpenSellerDrawerOptions) => {
  const { t } = useLocalization();

  const expandedSectionKeys = expandedSectionKey ? [expandedSectionKey] : [];

  const open = useOpenDrawer({
    title: t('h24_seller_drawer_headline'),
    content: (
      <Suspense fallback={<CenteredSpinner size={36} strokeColor="sys.neutral.border.hover" />}>
        <SellerDrawerContent
          shopId={shopId}
          isHome24={isHome24}
          withSellerKPIs={withSellerKPIs}
          expandedSectionKeys={expandedSectionKeys}
          ctaType={ctaType}
          sellerContactParams={sellerContactParams}
        />
      </Suspense>
    ),
  });

  const openDrawer = () => {
    if (tracking) {
      dispatchTrackingEventGA4(tracking);
    }

    open();
  };

  return { openDrawer };
};
